import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { usePageSeo } from "@/hooks/usePageSeo";
import { useLang } from "@/context/LanguageContext";
import { PageHero } from "@/components/common/PageHero";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarClock, Info } from "lucide-react";

const IMPACTS = ["all", "High", "Medium", "Low"];

export default function EconomicCalendar() {
  const { L } = useLang();
  const P = L.pages.calendar;
  usePageSeo("calendar");
  const { data } = useQuery({ queryKey: ["calendar"], queryFn: async () => (await api.get("/economic-calendar")).data });
  const [impact, setImpact] = useState("all");
  const events = data?.events || [];

  return (
    <>
      <PageHero
        overline={P.overline}
        title={P.title}
        description={P.desc}
        breadcrumbs={[{ label: L.common.home, to: "/" }, { label: P.title }]}
      />

      <section className="py-16 lg:py-24">
        <div className="container-tight">
          <div className="mb-6 grid gap-4 sm:grid-cols-4">
            {P.cols.slice(0, 3).map((l) => (
              <div key={l} className="space-y-1.5">
                <label className="text-xs font-medium uppercase tracking-wide text-subtle">{l}</label>
                <Select disabled><SelectTrigger><SelectValue placeholder={L.pages.brokerPartners.all} /></SelectTrigger><SelectContent /></Select>
              </div>
            ))}
            <div className="space-y-1.5">
              <label className="text-xs font-medium uppercase tracking-wide text-subtle">{P.impact}</label>
              <Select value={impact} onValueChange={setImpact}>
                <SelectTrigger data-testid="calendar-impact"><SelectValue /></SelectTrigger>
                <SelectContent>{IMPACTS.map((i) => <SelectItem key={i} value={i}>{i === "all" ? L.pages.brokerPartners.all : i}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="overflow-hidden rounded-xl border border-hair bg-white shadow-sm">
            <div className="grid grid-cols-7 border-b border-hair bg-navy px-4 py-3 text-xs font-semibold uppercase tracking-wide text-white">
              {P.cols.map((c, i) => <span key={c} className={i === 3 ? "col-span-2" : ""}>{c}</span>)}
            </div>
            {events.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
                <CalendarClock className="h-10 w-10 text-subtle/40" />
                <p className="font-heading text-lg font-semibold text-ink">{P.empty}</p>
                <p className="max-w-md text-sm text-subtle">{data?.note || ""}</p>
              </div>
            ) : (
              events.map((e, i) => (
                <div key={i} className="grid grid-cols-7 border-b border-hair px-4 py-3 text-sm">
                  <span>{e.time}</span><span>{e.currency}</span><span>{e.impact}</span>
                  <span className="col-span-2">{e.event}</span><span>{e.forecast}</span><span>{e.previous}</span>
                </div>
              ))
            )}
          </div>

          <div className="mt-6 flex gap-3 rounded-lg border border-hair bg-canvas p-4">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-finblue" />
            <p className="text-xs leading-relaxed text-subtle">{data?.note || ""}</p>
          </div>
        </div>
      </section>
    </>
  );
}
